import { useState } from "react";
import type { QueryKey } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import type { Attachment } from "@theta/api-client";
import { updatePost } from "@/lib/api/posts";

/**
 * ============================================================================
 * "FJERN VIDEO" — THE WAY OUT OF A TRANSCODE THAT FAILED (OSO-490 §5)
 * ============================================================================
 *
 * A post or comment whose video could not be encoded is held back: its
 * `published_at` is null, only its author can see it, and no sweep will ever
 * publish it, because the attachment that is blocking it will never become
 * ready. `media_failed` tells the author so — the window may well be closed by
 * then — and this is the action that notification leads to.
 *
 * IT IS AN ORDINARY EDIT, and deliberately so. `PATCH /posts/{id}` with the
 * attachments minus the failed one is the recovery path §2 describes: the same
 * route that would accept any other edit notices that nothing pending is left
 * and publishes the post, once, through the same `publishPost` the transcode
 * job calls. There is no "unstick this post" endpoint, and there should not
 * be — a second way to publish a row is a second place for the after-effects
 * (mentions, push, digests, the counters) to be got wrong.
 *
 * `bodyMarkdown` IS NOT SENT. Omitted fields are left alone (see `updatePost`),
 * and the author is not editing their text — sending the rendered body back
 * would be this component's guess at their markdown, which is exactly the
 * round trip §1.12 deleted.
 *
 * NOT FOR DMs. A message cannot be edited — nor, today, deleted — so a DM
 * whose video failed offers neither this action nor a way out; `MessageBubble`
 * says only what is true, that the message was not sent. OSO-564 is the ticket
 * for giving a sender the delete that would close it.
 */
export function useRemoveFailedVideo({
  postId,
  bodyHtml,
  attachments,
  invalidate,
}: {
  /** The post or comment row — a comment IS a post, both at `/posts/{id}`. */
  postId: number;
  /** The row's rendered body, to tell "an edit" from "nothing would be left". */
  bodyHtml: string;
  /** The row's current attachments, in display order. */
  attachments: Attachment[];
  /** Query keys to re-read once the post is published. */
  invalidate: QueryKey[];
}) {
  const queryClient = useQueryClient();
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * EVERY failed attachment goes, not just the one whose chip was pressed.
   * Two clips can fail on one post, and removing them one at a time would
   * leave the post held back after an action whose whole promise is that it
   * publishes — the member would press "Fjern video", watch nothing happen,
   * and have no way to tell which of the two was still in the way.
   */
  const kept = attachments.filter((a) => a.status !== "failed");

  /**
   * WHEN THE EDIT WOULD LEAVE NOTHING, IT IS NOT AN EDIT.
   *
   * A comment may be a video and nothing else — `CommentComposer` submits on
   * `body.trim() || files.hasContent`, so an attachment-only row is a shape
   * the composer produces on purpose. Take its one failed video away and the
   * PATCH composes to an empty body with no attachments, which is the exact
   * pair `POST/PATCH /posts` refuses with `bodyEmpty` (routes/v1/posts.ts).
   * The member would then be holding a button labelled "Fjern video" that
   * answers "Opslaget er tomt." and leaves the row stuck for good.
   *
   * So the caller is told, and offers the DELETE it already has instead —
   * relabelled, and behind its own confirm. Deleting is what the member is
   * actually asking for; the only dishonest version of this is a button that
   * says the other thing.
   *
   * `bodyHtml` and not `bodyMarkdown`: the thread rows carry only the rendered
   * body (`ThreadComment` has no `bodyMarkdown` — it is on the detail read,
   * behind `can.edit`), and `renderMarkdown("")` is `""`, so an empty render
   * IS an empty source. Tags are stripped before the test because a body of
   * one `<p></p>` renders nothing and must count as nothing.
   */
  const emptiesTheRow = kept.length === 0 && !hasVisibleBody(bodyHtml);

  const removeFailed = async () => {
    if (removing) return;
    setRemoving(true);
    setError(null);
    try {
      await updatePost(postId, {
        attachments: kept.map((a) => ({ uploadId: a.uploadId })),
      });
      for (const key of invalidate) {
        queryClient.invalidateQueries({ queryKey: key });
      }
    } catch (err) {
      setError(
        (err instanceof Error && err.message) ||
          "Videoen kunne ikke fjernes. Prøv igen."
      );
    } finally {
      setRemoving(false);
    }
  };

  return { removeFailed, removing, removeError: error, emptiesTheRow };
}

/**
 * Is there anything in this body but markup? `&nbsp;` is spelled out because
 * an empty paragraph is the one thing an editor reliably leaves behind, and it
 * is not text.
 */
export function hasVisibleBody(bodyHtml: string): boolean {
  return (
    bodyHtml
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim().length > 0
  );
}
